import { mousePerfCfgFeature } from '../../../../features/mouse-performance/contract'
import type {
  Correction,
  DcSwitch,
  GlassSurfaceSwitch,
  KeyDebounceTime,
  KeyReleaseDebounceTime,
  KeyScanRate,
  LinearCorrectionSwitch,
  LowLightEffectSwitch,
  MotionSynSwitch,
  MousePerfCfg,
  ReportRate,
  RfBoostSwitch,
  RfPacketType,
  RippleCorrectionSwitch,
  SensorAngle,
  SensorMode,
  SensorModes,
  SilentHeight,
  SleepTime,
} from '../../../../types/mousePerfCfg'
import { ADDRESS_VT_nrf54L } from '../../protocols/address'
import { sendRead, sendWrite } from '../../capabilities/memory'
import { getAddress } from '../../../../utils/common'

export class VTNrf54LPerfCfg extends mousePerfCfgFeature {
  // ==================== rf 发包处理类型 ====================
  async getRFProtocol(): Promise<RfPacketType> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.RF_PROTOCOL_SETTING, 0)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as RfPacketType
    })
  }
  async setRFProtocol(protocol: RfPacketType): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.RF_PROTOCOL_SETTING, 0)
    return sendWrite(this.device, ADDRESS, [protocol])
  }

  // ==================== RF 策略 ====================
  async getWirelessRadioPolicy(): Promise<RfBoostSwitch> {
    const ADDRESS = getAddress(
      ADDRESS_VT_nrf54L.RF_STRENGTHEN_SWITCH,
      this.device.currentConfig.index,
    )
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as RfBoostSwitch
    })
  }

  async setWirelessRadioPolicy(policy: RfBoostSwitch): Promise<void> {
    const ADDRESS = getAddress(
      ADDRESS_VT_nrf54L.RF_STRENGTHEN_SWITCH,
      this.device.currentConfig.index,
    )
    return sendWrite(this.device, ADDRESS, [policy])
  }

  // ==================== 回报率 ====================
  async getReportRate(): Promise<ReportRate> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_REPORT, this.device.currentConfig.index)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as ReportRate
    })
  }

  async setReportRate(rate: ReportRate): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_REPORT, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [rate])
  }

  // ==================== 传感器模式（与回报率索引对应） ====================
  async getSensorMode(index: number): Promise<SensorMode> {
    const ADDRESS = getAddress(
      ADDRESS_VT_nrf54L.MOUSE_REPORT_SENSOR_MODE,
      this.device.currentConfig.index,
      index,
    )
    return sendRead(
      this.device,
      ADDRESS,
      1,
      (res: DataView) => {
        return res.getUint8(4) as SensorMode
      },
      [`sensorMode:${this.device.currentConfig.index}`],
    )
  }

  async setSensorMode(index: number, mode: SensorMode): Promise<void> {
    const ADDRESS = getAddress(
      ADDRESS_VT_nrf54L.MOUSE_REPORT_SENSOR_MODE,
      this.device.currentConfig.index,
      index,
    )
    this.device.cache.invalidateByTag(`sensorMode:${this.device.currentConfig.index}`)
    return sendWrite(this.device, ADDRESS, [mode])
  }

  async getSensorModeList(): Promise<SensorModes> {
    const ADDRESS = getAddress(
      ADDRESS_VT_nrf54L.MOUSE_REPORT_SENSOR_MODE,
      this.device.currentConfig.index,
      0,
    )
    return sendRead(
      this.device,
      ADDRESS,
      7,
      (res: DataView) => {
        const modes = []
        for (let i = 0; i < 7; i++) {
          modes.push(res.getUint8(i + 4))
        }
        return modes as SensorModes
      },
      [`sensorMode:${this.device.currentConfig.index}`],
    )
  }

  async setSensorModeList(modes: SensorModes): Promise<void> {
    const ADDRESS = getAddress(
      ADDRESS_VT_nrf54L.MOUSE_REPORT_SENSOR_MODE,
      this.device.currentConfig.index,
      0,
    )
    this.device.cache.invalidateByTag(`sensorMode:${this.device.currentConfig.index}`)
    return sendWrite(this.device, ADDRESS, modes.slice(0, 7))
  }

  // ==================== 扫描率 ====================
  async getScanRate(): Promise<KeyScanRate> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_SCAN_RATE, this.device.currentConfig.index)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as KeyScanRate
    })
  }

  async setScanRate(rate: KeyScanRate): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_SCAN_RATE, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [rate])
  }

  // ==================== 移动同步 ====================
  async getMoveSync(): Promise<MotionSynSwitch> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_MOTION, this.device.currentConfig.index)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as MotionSynSwitch
    })
  }

  async setMoveSync(sync: MotionSynSwitch): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_MOTION, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [sync])
  }

  // ==================== 直线/波纹修正 ====================
  async getCorrect(): Promise<Correction> {
    // bit 0 直线 bit 1 波纹
    // 0 打开 1 关闭
    const ADDRESS = getAddress(
      ADDRESS_VT_nrf54L.MOUSE_LINEAR_RIPPLE,
      this.device.currentConfig.index,
    )
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      const buffer = res.getUint8(4)
      return {
        linearCorrectionSwitch: (buffer & 1) as LinearCorrectionSwitch,
        rippleCorrectionSwitch: ((buffer & 2) >> 1) as RippleCorrectionSwitch,
      }
    })
  }

  async setCorrect(correct: Correction): Promise<void> {
    // bit 0 直线 bit 1 波纹
    // 0 打开 1 关闭
    const ADDRESS = getAddress(
      ADDRESS_VT_nrf54L.MOUSE_LINEAR_RIPPLE,
      this.device.currentConfig.index,
    )
    const buffer = correct.linearCorrectionSwitch | (correct.rippleCorrectionSwitch << 1)
    return sendWrite(this.device, ADDRESS, [buffer])
  }

  // ==================== 休眠时间 ====================
  async getSleepTime(): Promise<SleepTime> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_SLEEPTIME, this.device.currentConfig.index)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as SleepTime
    })
  }

  async setSleepTime(time: SleepTime): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_SLEEPTIME, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [time])
  }

  // ==================== 传感器角度 ====================
  async getSensorAngle(): Promise<SensorAngle> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_SENSORANGLE, this.device.currentConfig.index)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getInt8(4) as SensorAngle
    })
  }

  async setSensorAngle(angle: SensorAngle): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_SENSORANGLE, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [angle])
  }

  // ==================== 玻璃面追踪 ====================
  async getGlassTrack(): Promise<GlassSurfaceSwitch> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_GLASS, this.device.currentConfig.index)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as GlassSurfaceSwitch
    })
  }

  async setGlassTrack(track: GlassSurfaceSwitch): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_GLASS, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [track])
  }

  // ==================== 按下消抖时间 ====================
  async getDebounceTime(): Promise<KeyDebounceTime> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_DOWNDELAY, this.device.currentConfig.index)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as KeyDebounceTime
    })
  }

  async setDebounceTime(time: KeyDebounceTime): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_DOWNDELAY, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [time])
  }

  // ==================== 释放消抖时间 ====================
  async getReleaseDebounceTime(): Promise<KeyReleaseDebounceTime> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_LIFTDELAY, this.device.currentConfig.index)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as KeyReleaseDebounceTime
    })
  }

  async setReleaseDebounceTime(time: KeyReleaseDebounceTime): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_LIFTDELAY, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [time])
  }

  // ==================== 静默高度 ====================
  async getSilentHeight(): Promise<SilentHeight> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_SLIGHT, this.device.currentConfig.index)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as SilentHeight
    })
  }

  async setSilentHeight(height: SilentHeight): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.MOUSE_SLIGHT, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [height])
  }

  // ==================== 低电灯效 ====================
  async getLowBatteryLight(): Promise<LowLightEffectSwitch> {
    const ADDRESS = getAddress(
      ADDRESS_VT_nrf54L.LOW_POWE_WARN_SWITCH,
      this.device.currentConfig.index,
    )
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as LowLightEffectSwitch
    })
  }

  async setLowBatteryLight(light: LowLightEffectSwitch): Promise<void> {
    const ADDRESS = getAddress(
      ADDRESS_VT_nrf54L.LOW_POWE_WARN_SWITCH,
      this.device.currentConfig.index,
    )
    return sendWrite(this.device, ADDRESS, [light])
  }

  // ==================== DC 开关 ====================
  async getDcSwitch(): Promise<DcSwitch> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.LOW_DC_SWITCH, this.device.currentConfig.index)
    return sendRead(this.device, ADDRESS, 1, (res: DataView) => {
      return res.getUint8(4) as DcSwitch
    })
  }

  async setDcSwitch(dc: DcSwitch): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_VT_nrf54L.LOW_DC_SWITCH, this.device.currentConfig.index)
    return sendWrite(this.device, ADDRESS, [dc])
  }

  // ==================== 批量获取 ====================
  async getAllPerfCfg(): Promise<MousePerfCfg> {
    const cfg: MousePerfCfg = {}
    const cacheTag = [`cfg:${this.device.currentConfig.index}`]

    // 0x0060（rf 发包处理类型）
    cfg.rfPacketType = await this.getRFProtocol()

    // 0x0280 ~ 0x0287 (包含回报率、键扫描率、静默高度、MOTION SYN 开关)
    const ADDRESS1 = getAddress(ADDRESS_VT_nrf54L.MOUSE_REPORT, this.device.currentConfig.index)
    await sendRead(
      this.device,
      ADDRESS1,
      8,
      (res: DataView) => {
        // 回报率
        cfg.reportRate = res.getUint8(4) as ReportRate
        // 键扫描率
        cfg.keyScanRate = res.getUint8(5) as KeyScanRate
        // 静默高度
        cfg.silentHeight = res.getUint8(8) as SilentHeight
        // MOTION SYN 开关
        cfg.motionSynSwitch = res.getUint8(9) as MotionSynSwitch
      },
      cacheTag,
    )

    // 0x02C0 ~ 0x02C5 (包含按下消抖时间、释放消抖时间、休眠时间、直线/波纹修正开关、传感器角度、玻璃面)
    const ADDRESS2 = getAddress(ADDRESS_VT_nrf54L.MOUSE_DOWNDELAY, this.device.currentConfig.index)
    await sendRead(
      this.device,
      ADDRESS2,
      6,
      (res: DataView) => {
        // 按下消抖时间
        cfg.keyDebounceTime = res.getUint8(4) as KeyDebounceTime
        // 释放消抖时间
        cfg.keyReleaseDebounceTime = res.getUint8(5) as KeyReleaseDebounceTime
        // 休眠时间
        cfg.sleepTime = res.getUint8(6) as SleepTime
        const correct = res.getUint8(7)
        // 直线修正开关
        cfg.linearCorrectionSwitch = (correct & 1) as LinearCorrectionSwitch
        // 波纹修正开关
        cfg.rippleCorrectionSwitch = ((correct & 2) >> 1) as RippleCorrectionSwitch
        // 传感器角度
        cfg.sensorAngle = res.getInt8(8) as SensorAngle
        // 玻璃面
        cfg.glassSurfaceSwitch = res.getUint8(9) as GlassSurfaceSwitch
      },
      cacheTag,
    )

    // 0x02D8 ~ 0x02DB (包含RF 强化开/关、低光效果开/关、DC 开/关)
    const ADDRESS3 = getAddress(
      ADDRESS_VT_nrf54L.RF_STRENGTHEN_SWITCH,
      this.device.currentConfig.index,
    )
    await sendRead(
      this.device,
      ADDRESS3,
      4,
      (res: DataView) => {
        // RF 强化开/关
        cfg.rfBoostSwitch = res.getUint8(4) as RfBoostSwitch
        // 低光效果开/关
        cfg.lowLightEffectSwitch = res.getUint8(5) as LowLightEffectSwitch
        // DC 开/关
        cfg.dcSwitch = res.getUint8(6) as DcSwitch
      },
      cacheTag,
    )

    return cfg
  }

  // ==================== 批量设置 ====================
  async setAllPerfCfg(cfg: MousePerfCfg): Promise<void> {
    // 仅对传入对象中存在的字段（非 undefined）调用对应的 set 方法

    if (cfg.rfPacketType !== undefined) {
      await this.setRFProtocol(cfg.rfPacketType)
    }

    if (cfg.reportRate !== undefined) {
      await this.setReportRate(cfg.reportRate)
    }

    if (cfg.keyScanRate !== undefined) {
      await this.setScanRate(cfg.keyScanRate)
    }

    if (cfg.silentHeight !== undefined) {
      await this.setSilentHeight(cfg.silentHeight)
    }

    if (cfg.motionSynSwitch !== undefined) {
      await this.setMoveSync(cfg.motionSynSwitch)
    }

    if (cfg.keyDebounceTime !== undefined) {
      await this.setDebounceTime(cfg.keyDebounceTime)
    }

    if (cfg.keyReleaseDebounceTime !== undefined) {
      await this.setReleaseDebounceTime(cfg.keyReleaseDebounceTime)
    }

    if (cfg.sleepTime !== undefined) {
      await this.setSleepTime(cfg.sleepTime)
    }

    if (cfg.linearCorrectionSwitch !== undefined && cfg.rippleCorrectionSwitch !== undefined) {
      await this.setCorrect({
        linearCorrectionSwitch: cfg.linearCorrectionSwitch,
        rippleCorrectionSwitch: cfg.rippleCorrectionSwitch,
      })
    }

    if (cfg.sensorAngle !== undefined) {
      await this.setSensorAngle(cfg.sensorAngle)
    }

    if (cfg.glassSurfaceSwitch !== undefined) {
      await this.setGlassTrack(cfg.glassSurfaceSwitch)
    }

    if (cfg.rfBoostSwitch !== undefined) {
      await this.setWirelessRadioPolicy(cfg.rfBoostSwitch)
    }

    if (cfg.lowLightEffectSwitch !== undefined) {
      await this.setLowBatteryLight(cfg.lowLightEffectSwitch)
    }

    if (cfg.dcSwitch !== undefined) {
      await this.setDcSwitch(cfg.dcSwitch)
    }

    // 清除 getAllPerfCfg() 批量读取的三个大地址区间缓存(:8/:6/:4)
    // 这些大缓存带的 cfg tag 与单个 set 方法触发的 address-range 失效不重叠
    this.device.cache.invalidateByTag(`cfg:${this.device.currentConfig.index}`)
  }
}
